/**
 * Shared types + URL builder for the admin Audit Search endpoints
 * (`api/<resource>/audits/search`). Every resource's `search…Audits` fn is a
 * thin wrapper over `buildAuditSearchUrl` so the (legacy, hand-built) URL
 * convention lives in exactly one place.
 *
 * The `^`-prefixed predicate keys (`^auditType`, `^attributes.<field>`) must
 * stay URL-unencoded — that prefix is the server's faceted-filter marker — so
 * we assemble the query string by hand rather than via params serialization.
 */

/** Structured filters shared by every audit tab (epic #2018). */
export interface AuditFilters {
  /** Actor urn — `subject=urn:brew:profiles:<id>`. */
  subject?: string;
  /** Resource urn — `object=urn:brew:<type>:<id>` (CRUD verbs). */
  object?: string;
  /** Inclusive lower timestamp bound, ISO-8601 instant. */
  since?: string;
  /** Inclusive upper timestamp bound, ISO-8601 instant. */
  until?: string;
  /** Per-verb facet equality filters → `^attributes.<key>=<value>`. */
  attrs?: Record<string, string>;
}

export interface AuditSearchParams<V extends string = string> {
  auditType?: V;
  text?: string;
  limit?: number;
  skip?: number;
  sort?: string;
  filters?: AuditFilters;
}

/**
 * Assemble an `…/audits/search` URL. Omit `auditType` to fetch every verb
 * (the ALL view). Filters append `subject`/`object`/`since`/`until` and one
 * `^attributes.<key>=<value>` per facet.
 */
export function buildAuditSearchUrl(base: string, params: AuditSearchParams = {}): string {
  const { auditType, text = '*', limit = 20, skip = 0, sort = '-timestamp', filters } = params;

  let url =
    base +
    (auditType ? `?^auditType=${encodeURIComponent(auditType)}&` : '?') +
    `text=${encodeURIComponent(text)}` +
    `&limit=${limit}` +
    `&skip=${skip}` +
    `&sort=${sort}`;

  if (filters) {
    // `subject`/`object` are real audit fields but are NOT plain query params on
    // the global search endpoint (they're constructor-injected for per-resource
    // mounts), so filter them through the `^` faceted-equality mechanism — same
    // as `^attributes.*`. `since`/`until` ARE plain params (handled in
    // AuditResource.getQueryBuilder).
    if (filters.subject) url += `&^subject=${encodeURIComponent(filters.subject)}`;
    if (filters.object) url += `&^object=${encodeURIComponent(filters.object)}`;
    if (filters.since) url += `&since=${encodeURIComponent(filters.since)}`;
    if (filters.until) url += `&until=${encodeURIComponent(filters.until)}`;
    if (filters.attrs) {
      for (const [key, value] of Object.entries(filters.attrs)) {
        if (value !== undefined && value !== null && value !== '') {
          // `^attributes.<key>` stays unencoded (faceted-filter marker); value encoded.
          url += `&^attributes.${key}=${encodeURIComponent(value)}`;
        }
      }
    }
  }

  return url;
}
